import {useLocation} from "react-router";

import {IAppError} from "@pg-mono/request-state";

import {createRpLeadPerformanceDebugTrace} from "../../app/performance_debug";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorInstalmentEventAction} from "../../credit_calculator/tracking/credit_gtm";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {useGetOfferDetailApplicationQuery} from "../../offer/api/get_offer_detail";
import {useGetPropertyDetailApplicationQuery} from "../../property/api/get_property_detail";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {hitRotatingApplicationError} from "../../tracking/algolytics_hits/application_hit";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useGetVendorDetailApplicationQuery} from "../../vendor/api/get_vendor_detail_application_api";
import {setSentLeadValuesAction} from "../reducers/sent_lead_reducer";
import {trackAmplitudeLeadFormError} from "../tracking/amplitude/track_amplitude_lead_form_events";
import {hitLeadSent} from "../tracking/application_form_hit_lead_sent";
import {ApplicationFormVariant} from "../types/form/ApplicationFormVariant";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {ISendMainLeadFormValues} from "../types/lead/ISendMainLeadFormValues";
import {alertFormArchivedOfferMessage, alertFormSuccessMessage} from "../utils/form/application_form_alert_text";
import {ApplicationSource} from "../utils/form/application_form_source";
import {getGeneratedMessage} from "../utils/form/get_generated_message";
import {markLeadFormSubmitErrorTracked} from "../utils/is_lead_form_submit_app_error";
import {getLeadAnalyticsData} from "../utils/lead/get_lead_analytics_data";
import {getParsedLeadFormValues} from "../utils/lead/get_parsed_lead_form_values";
import {sendLead} from "../utils/lead/send_lead";
import {useApplicationVariant} from "./use_application_form_variant";
import {useLeadRequiredParams} from "./use_lead_required_params";

export function useSendMainLead() {
    const dispatch = useAppDispatch();

    const {vendorId, offerId, propertyId, source, sourceSection} = useLeadRequiredParams("property");

    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId, {skip: !vendorId});
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId, {skip: !offerId});
    const {data: propertyDetail} = useGetPropertyDetailApplicationQuery(propertyId, {skip: !propertyId});

    const location = useLocation();

    const affiliation = useAppSelector((state) => state.mainLead.affiliation);
    const creditPayload = useAppSelector((state) => state.mainLead.creditPayload);
    const customFormText = useAppSelector((state) => state.mainLead.customFormText);
    const leadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const amplitudeLeadFormEventTracking = useAppSelector((state) => state.mainLead.amplitudeLeadFormEventTracking);
    const salesOfficeId = useAppSelector((state) => state.mainLead.salesOfficeId);
    const applicationVariant = useApplicationVariant();

    const viewType = useAppSelector((state) => state.viewType.current);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfileData} = useGetUserProfileQuery();

    const leadData = {
        offer: {detail: offerDetail},
        property: {detail: propertyDetail},
        vendor: {detail: vendorDetail}
    };

    const analyticsData = getLeadAnalyticsData(leadData, viewType, source, sourceSection);

    // TODO: Until we rewrite/remove Application, keep this inline with logic in Application
    return async (
        formValues: ISendMainLeadFormValues,
        handleModalClose?: (forceClose: boolean) => void,
        options?: {
            analyticsParams?: Parameters<typeof sendLead>[0]["analyticsParams"];
            persistFormValues?: boolean;
            showSuccessNotification?: boolean;
        }
    ) => {
        const showSuccessNotification = options?.showSuccessNotification !== false;
        const trace = createRpLeadPerformanceDebugTrace("rp_use_send_main_lead", {
            leadType,
            offerId,
            propertyId,
            source,
            sourceSection,
            vendorId
        });
        const isVendorOnly = source === ApplicationSource.Vendor && !offerId && !propertyId;
        trace.mark("lead_values:prepare_start", {
            hasCustomFormText: Boolean(customFormText),
            isVendorOnly,
            persistFormValues: options?.persistFormValues !== false
        });

        const leadFormValues: IApplicationFormValues = {
            name: formValues.name,
            email: formValues.email,
            phone: {
                number: formValues.phone.number,
                code: formValues.phone.code
            },
            text:
                customFormText ||
                getGeneratedMessage({
                    offerType: offerDetail?.type,
                    offerName: offerDetail?.name,
                    propertyNumber: propertyDetail?.number,
                    name: formValues.name,
                    rooms: propertyDetail?.rooms,
                    area: propertyDetail?.area,
                    negotiation: formValues.negotiation,
                    meetingDay: formValues.meetingDay,
                    currency: offerDetail?.currency,
                    additionalQuestions: formValues.additionalQuestions?.map((question) => question.label),
                    message: formValues.message,
                    isVendorOnly
                }),
            marketing_email: formValues.marketing_email,
            marketing_sms: formValues.marketing_sms,
            // TODO if it will be used in offers, add rooms range handling
            rooms: [],
            negotiation: formValues.negotiation,
            meetingDay: formValues.meetingDay,
            additionalQuestions: formValues.additionalQuestions
        };
        trace.mark("lead_values:prepared", {
            hasAdditionalQuestions: Boolean(formValues.additionalQuestions?.length),
            hasMessage: Boolean(formValues.message),
            hasMeetingDay: Boolean(formValues.meetingDay),
            hasNegotiation: Boolean(formValues.negotiation)
        });

        if (options?.persistFormValues !== false) {
            dispatch(setSentLeadValuesAction(getParsedLeadFormValues(leadFormValues)));
            trace.mark("sent_lead_values:persisted");
        }

        // Flows that send without opening the lead modal (eg. LeadAfterRegistration) pass their own
        // context, so fall back to it when the store has none.
        const amplitudeErrorTrackingContext = amplitudeLeadFormEventTracking ?? options?.analyticsParams?.amplitudeLeadFormEventTracking ?? null;

        const trackLeadFormError = (appError: IAppError, errorCode?: string): boolean => {
            if (!amplitudeErrorTrackingContext || source == null || sourceSection == null) {
                return false;
            }

            trackAmplitudeLeadFormError({
                trackingContext: amplitudeErrorTrackingContext,
                leadData,
                source,
                sourceSection,
                appError,
                ...(errorCode ? {errorCode} : {})
            });

            return true;
        };
        // A 400 is rethrown to the form, which reports API errors of its own - but every lead modal
        // submits through the draft flow, which swallows the error before it gets there (RP-771).
        let didReport400 = false;

        try {
            trace.mark("send_lead:start");

            const response = await sendLead({
                dispatch,
                leadData,
                leadFormValues,
                leadMeta: {
                    source,
                    sourceSection,
                    locationHash: location.hash,
                    viewType
                },
                leadType: leadType || "standard",
                isAuthenticated,
                userProfileData,
                senderState: {
                    generatedMessage: null,
                    questions: []
                },
                analyticsData,
                analyticsParams: {
                    ...options?.analyticsParams,
                    ...(amplitudeLeadFormEventTracking ? {amplitudeLeadFormEventTracking} : {})
                },
                affiliation: affiliation || undefined,
                realEstateIds: {
                    vendorId,
                    offerId,
                    propertyId,
                    salesOfficeId: salesOfficeId || undefined
                },
                creditData: creditPayload || undefined,
                showSuccessNotification,
                onSendSuccess: (createdApplication) => {
                    trace.mark("on_send_success:start", {
                        userCreated: Boolean(createdApplication.user_created)
                    });

                    // TODO: Credit calculator logic should be moved to the credit calculator
                    if (applicationVariant !== ApplicationFormVariant.CREDIT) {
                        const hasMktOptin = Boolean(createdApplication.marketing_email || createdApplication.marketing_sms);
                        hitLeadSent(hasMktOptin);
                        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                            credit_action: CreditCalculatorInstalmentEventAction.CALL
                        });
                    }

                    if (showSuccessNotification) {
                        dispatch(addSuccessNotification(alertFormSuccessMessage));
                    }
                    trace.mark("on_send_success:done");
                },
                postOptions: {
                    on400ApplicationBlockedError: (error) => {
                        trace.mark("post_options:on400_application_blocked");
                        handleModalClose?.(true);
                        dispatch(addWarningNotification(alertFormArchivedOfferMessage, 7000));

                        // The blocked-offer payload nests its message under a numeric key, which would be
                        // reported as a bogus `error_fields: ["0"]`; report what the user actually saw.
                        trackLeadFormError({status: error.status, fieldErrors: {}, nonFieldErrors: [alertFormArchivedOfferMessage]});

                        hitRotatingApplicationError(source, sourceSection, leadData, {
                            appError: {fieldErrors: error.fieldErrors, status: error.status, nonFieldErrors: error.nonFieldErrors} as IAppError,
                            extraPayloadViewType: viewType ?? undefined
                        });

                        // TODO: remove this bugsnag notification when we confirm that blocking applications this way is not an issue
                        notifyBugsnag(
                            {
                                name: "Application blocked info",
                                message: "Application blocked info"
                            },
                            "Application sent on an archived offer",
                            {error}
                        );
                        setTimeout(() => window.location.reload(), 6000);
                    },
                    on400Response: (error) => {
                        trace.mark("post_options:on400_response");
                        didReport400 = trackLeadFormError(error as IAppError);

                        hitRotatingApplicationError(source, sourceSection, leadData, {
                            appError: {fieldErrors: error.fieldErrors, status: error.status, nonFieldErrors: error.nonFieldErrors} as IAppError,
                            extraPayloadViewType: viewType ?? undefined
                        });
                    },
                    // 400s are handled above; everything else ends here, where the lead is silently
                    // dropped, so this is the only chance to report it.
                    onUnhandledResponseError: ({appError, errorCode}) => {
                        trace.mark("post_options:on_unhandled_response_error", {errorCode});
                        trackLeadFormError(appError, errorCode);
                    }
                }
            });

            trace.end("send_lead:done", {
                hasResponse: Boolean(response),
                userCreated: Boolean(response?.user_created)
            });

            return response;
        } catch (error) {
            trace.end("send_lead:error");

            // Reported above - mark it so the form's own error handler doesn't report it a second time.
            throw didReport400 ? markLeadFormSubmitErrorTracked(error) : error;
        }
    };
}
import {useLocation} from "react-router";

import {IAppError} from "@pg-mono/request-state";

import {createRpLeadPerformanceDebugTrace} from "../../app/performance_debug";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorInstalmentEventAction} from "../../credit_calculator/tracking/credit_gtm";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {useGetOfferDetailApplicationQuery} from "../../offer/api/get_offer_detail";
import {useGetPropertyDetailApplicationQuery} from "../../property/api/get_property_detail";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {hitRotatingApplicationError} from "../../tracking/algolytics_hits/application_hit";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useGetVendorDetailApplicationQuery} from "../../vendor/api/get_vendor_detail_application_api";
import {setSentLeadValuesAction} from "../reducers/sent_lead_reducer";
import {trackAmplitudeLeadFormError} from "../tracking/amplitude/track_amplitude_lead_form_events";
import {hitLeadSent} from "../tracking/application_form_hit_lead_sent";
import {ApplicationFormVariant} from "../types/form/ApplicationFormVariant";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {ISendMainLeadFormValues} from "../types/lead/ISendMainLeadFormValues";
import {alertFormArchivedOfferMessage, alertFormSuccessMessage} from "../utils/form/application_form_alert_text";
import {ApplicationSource} from "../utils/form/application_form_source";
import {getGeneratedMessage} from "../utils/form/get_generated_message";
import {markLeadFormSubmitErrorTracked} from "../utils/is_lead_form_submit_app_error";
import {getLeadAnalyticsData} from "../utils/lead/get_lead_analytics_data";
import {getParsedLeadFormValues} from "../utils/lead/get_parsed_lead_form_values";
import {sendLead} from "../utils/lead/send_lead";
import {useApplicationVariant} from "./use_application_form_variant";
import {useLeadRequiredParams} from "./use_lead_required_params";

export function useSendMainLead() {
    const dispatch = useAppDispatch();

    const {vendorId, offerId, propertyId, source, sourceSection} = useLeadRequiredParams("property");

    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId, {skip: !vendorId});
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId, {skip: !offerId});
    const {data: propertyDetail} = useGetPropertyDetailApplicationQuery(propertyId, {skip: !propertyId});

    const location = useLocation();

    const affiliation = useAppSelector((state) => state.mainLead.affiliation);
    const creditPayload = useAppSelector((state) => state.mainLead.creditPayload);
    const customFormText = useAppSelector((state) => state.mainLead.customFormText);
    const leadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const amplitudeLeadFormEventTracking = useAppSelector((state) => state.mainLead.amplitudeLeadFormEventTracking);
    const salesOfficeId = useAppSelector((state) => state.mainLead.salesOfficeId);
    const applicationVariant = useApplicationVariant();

    const viewType = useAppSelector((state) => state.viewType.current);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfileData} = useGetUserProfileQuery();

    const leadData = {
        offer: {detail: offerDetail},
        property: {detail: propertyDetail},
        vendor: {detail: vendorDetail}
    };

    const analyticsData = getLeadAnalyticsData(leadData, viewType, source, sourceSection);

    // TODO: Until we rewrite/remove Application, keep this inline with logic in Application
    return async (
        formValues: ISendMainLeadFormValues,
        handleModalClose?: (forceClose: boolean) => void,
        options?: {
            analyticsParams?: Parameters<typeof sendLead>[0]["analyticsParams"];
            persistFormValues?: boolean;
            showSuccessNotification?: boolean;
        }
    ) => {
        const showSuccessNotification = options?.showSuccessNotification !== false;
        const trace = createRpLeadPerformanceDebugTrace("rp_use_send_main_lead", {
            leadType,
            offerId,
            propertyId,
            source,
            sourceSection,
            vendorId
        });
        const isVendorOnly = source === ApplicationSource.Vendor && !offerId && !propertyId;
        trace.mark("lead_values:prepare_start", {
            hasCustomFormText: Boolean(customFormText),
            isVendorOnly,
            persistFormValues: options?.persistFormValues !== false
        });

        const leadFormValues: IApplicationFormValues = {
            name: formValues.name,
            email: formValues.email,
            phone: {
                number: formValues.phone.number,
                code: formValues.phone.code
            },
            text:
                customFormText ||
                getGeneratedMessage({
                    offerType: offerDetail?.type,
                    offerName: offerDetail?.name,
                    propertyNumber: propertyDetail?.number,
                    name: formValues.name,
                    rooms: propertyDetail?.rooms,
                    area: propertyDetail?.area,
                    negotiation: formValues.negotiation,
                    meetingDay: formValues.meetingDay,
                    currency: offerDetail?.currency,
                    additionalQuestions: formValues.additionalQuestions?.map((question) => question.label),
                    message: formValues.message,
                    isVendorOnly
                }),
            marketing_email: formValues.marketing_email,
            marketing_sms: formValues.marketing_sms,
            // TODO if it will be used in offers, add rooms range handling
            rooms: [],
            negotiation: formValues.negotiation,
            meetingDay: formValues.meetingDay,
            additionalQuestions: formValues.additionalQuestions
        };
        trace.mark("lead_values:prepared", {
            hasAdditionalQuestions: Boolean(formValues.additionalQuestions?.length),
            hasMessage: Boolean(formValues.message),
            hasMeetingDay: Boolean(formValues.meetingDay),
            hasNegotiation: Boolean(formValues.negotiation)
        });

        if (options?.persistFormValues !== false) {
            dispatch(setSentLeadValuesAction(getParsedLeadFormValues(leadFormValues)));
            trace.mark("sent_lead_values:persisted");
        }

        // Flows that send without opening the lead modal (eg. LeadAfterRegistration) pass their own
        // context, so fall back to it when the store has none.
        const amplitudeErrorTrackingContext = amplitudeLeadFormEventTracking ?? options?.analyticsParams?.amplitudeLeadFormEventTracking ?? null;

        const trackLeadFormError = (appError: IAppError, errorCode?: string): boolean => {
            if (!amplitudeErrorTrackingContext || source == null || sourceSection == null) {
                return false;
            }

            trackAmplitudeLeadFormError({
                trackingContext: amplitudeErrorTrackingContext,
                leadData,
                source,
                sourceSection,
                appError,
                ...(errorCode ? {errorCode} : {})
            });

            return true;
        };
        // A 400 is rethrown to the form, which reports API errors of its own - but every lead modal
        // submits through the draft flow, which swallows the error before it gets there (RP-771).
        let didReport400 = false;

        try {
            trace.mark("send_lead:start");

            const response = await sendLead({
                dispatch,
                leadData,
                leadFormValues,
                leadMeta: {
                    source,
                    sourceSection,
                    locationHash: location.hash,
                    viewType
                },
                leadType: leadType || "standard",
                isAuthenticated,
                userProfileData,
                senderState: {
                    generatedMessage: null,
                    questions: []
                },
                analyticsData,
                analyticsParams: {
                    ...options?.analyticsParams,
                    ...(amplitudeLeadFormEventTracking ? {amplitudeLeadFormEventTracking} : {})
                },
                affiliation: affiliation || undefined,
                realEstateIds: {
                    vendorId,
                    offerId,
                    propertyId,
                    salesOfficeId: salesOfficeId || undefined
                },
                creditData: creditPayload || undefined,
                showSuccessNotification,
                onSendSuccess: (createdApplication) => {
                    trace.mark("on_send_success:start", {
                        userCreated: Boolean(createdApplication.user_created)
                    });

                    // TODO: Credit calculator logic should be moved to the credit calculator
                    if (applicationVariant !== ApplicationFormVariant.CREDIT) {
                        const hasMktOptin = Boolean(createdApplication.marketing_email || createdApplication.marketing_sms);
                        hitLeadSent(hasMktOptin);
                        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
                            credit_action: CreditCalculatorInstalmentEventAction.CALL
                        });
                    }

                    if (showSuccessNotification) {
                        dispatch(addSuccessNotification(alertFormSuccessMessage));
                    }
                    trace.mark("on_send_success:done");
                },
                postOptions: {
                    on400ApplicationBlockedError: (error) => {
                        trace.mark("post_options:on400_application_blocked");
                        handleModalClose?.(true);
                        dispatch(addWarningNotification(alertFormArchivedOfferMessage, 7000));

                        // The blocked-offer payload nests its message under a numeric key, which would be
                        // reported as a bogus `error_fields: ["0"]`; report what the user actually saw.
                        trackLeadFormError({status: error.status, fieldErrors: {}, nonFieldErrors: [alertFormArchivedOfferMessage]});

                        hitRotatingApplicationError(source, sourceSection, leadData, {
                            appError: {fieldErrors: error.fieldErrors, status: error.status, nonFieldErrors: error.nonFieldErrors} as IAppError,
                            extraPayloadViewType: viewType ?? undefined
                        });

                        // TODO: remove this bugsnag notification when we confirm that blocking applications this way is not an issue
                        notifyBugsnag(
                            {
                                name: "Application blocked info",
                                message: "Application blocked info"
                            },
                            "Application sent on an archived offer",
                            {error}
                        );
                        setTimeout(() => window.location.reload(), 6000);
                    },
                    on400Response: (error) => {
                        trace.mark("post_options:on400_response");
                        didReport400 = trackLeadFormError(error as IAppError);

                        hitRotatingApplicationError(source, sourceSection, leadData, {
                            appError: {fieldErrors: error.fieldErrors, status: error.status, nonFieldErrors: error.nonFieldErrors} as IAppError,
                            extraPayloadViewType: viewType ?? undefined
                        });
                    },
                    // 400s are handled above; everything else ends here, where the lead is silently
                    // dropped, so this is the only chance to report it.
                    onUnhandledResponseError: ({appError, errorCode}) => {
                        trace.mark("post_options:on_unhandled_response_error", {errorCode});
                        trackLeadFormError(appError, errorCode);
                    }
                }
            });

            trace.end("send_lead:done", {
                hasResponse: Boolean(response),
                userCreated: Boolean(response?.user_created)
            });

            return response;
        } catch (error) {
            trace.end("send_lead:error");

            // Reported above - mark it so the form's own error handler doesn't report it a second time.
            throw didReport400 ? markLeadFormSubmitErrorTracked(error) : error;
        }
    };
}
